import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import BrandLogo from './BrandLogo';
import ClubLogo from './ClubLogo';

const ROLE_LABELS = { coach: 'Coach', analyst: 'Analyst', club_admin: 'Club Admin' };

export default function InviteAcceptPage({ isLoggedIn, user }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setError('No invite token provided.');
      setLoading(false);
      return;
    }

    api.get('/invites/validate/' + token)
      .then(res => {
        setInviteData(res.data.invite);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.error || 'Invalid or expired invite link.');
        setLoading(false);
      });
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    setError('');
    try {
      const res = await api.post('/invites/accept', { token });
      setSuccess(res.data.message || 'Invite accepted!');

      // Update stored user to reflect club membership and any promoted role
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        stored.hasClub = true;
        if (res.data.role) stored.role = res.data.role;
        localStorage.setItem('user', JSON.stringify(stored));
      } catch {}

      // Redirect to dashboard after short delay
      setTimeout(() => window.location.href = '/', 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept invite.');
    }
    setAccepting(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Inter, sans-serif',
    }}>
      <div style={{
        background: 'var(--bg-card)',
        padding: window.innerWidth < 480 ? '24px' : '40px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '480px',
        border: '1px solid var(--border-color)',
        textAlign: 'center',
      }}>
        <div style={{ marginBottom: '24px' }}>
          <BrandLogo height={54} showBeta />
          <p style={{ color: 'var(--text-dim)', fontSize: '13px', margin: '10px 0 0' }}>
            Club Invitation
          </p>
        </div>

        {loading && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            Validating invite...
          </p>
        )}

        {error && (
          <div role="alert" style={{
            background: '#450a0a',
            border: '1px solid #dc2626',
            color: '#fca5a5',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
          }}>
            <strong>Error: </strong>{error}
          </div>
        )}

        {success && (
          <div role="status" aria-live="polite" style={{
            background: '#134E4A',
            border: '1px solid #0D9488',
            color: '#5EEAD4',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
          }}>
            <strong>Success: </strong>{success}
          </div>
        )}

        {!loading && !error && inviteData && !success && (
          <>
            <div style={{
              background: 'var(--bg-input)',
              border: '1px solid var(--border-input)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                <ClubLogo
                  club={{
                    name: inviteData.clubName,
                    logoUrl: inviteData.clubLogoUrl,
                    logoUpdatedAt: inviteData.clubLogoUpdatedAt
                  }}
                  size="lg"
                  decorative={false}
                />
              </div>
              <p style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: 600, margin: '0 0 8px 0' }}>
                {inviteData.clubName}
              </p>
              {inviteData.clubSport && (
                <p style={{ color: 'var(--text-dim)', fontSize: '13px', margin: '0 0 12px 0' }}>
                  {inviteData.clubSport}
                </p>
              )}
              <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>
                Invited as: <strong style={{ color: 'var(--accent)' }}>{ROLE_LABELS[inviteData.role] || inviteData.role}</strong>
              </p>
              <p style={{ color: 'var(--text-dim)', fontSize: '12px', margin: '8px 0 0 0' }}>
                Invite for: {inviteData.email}
              </p>
            </div>

            {isLoggedIn ? (
              <button
                onClick={handleAccept}
                disabled={accepting}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: accepting ? 'var(--border-input)' : 'var(--accent)',
                  color: 'var(--text-on-accent)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: accepting ? 'not-allowed' : 'pointer',
                  marginBottom: '12px',
                }}
              >
                {accepting ? 'Accepting...' : 'Accept Invite'}
              </button>
            ) : (
              <div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
                  Sign in or create an account to accept this invite.
                </p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Link
                    to={'/login?redirect=' + encodeURIComponent('/invite?token=' + token)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'var(--accent)',
                      color: 'var(--text-on-accent)',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      textAlign: 'center',
                    }}
                  >
                    Sign In
                  </Link>
                  <Link
                    to={'/register?redirect=' + encodeURIComponent('/invite?token=' + token)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'transparent',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-input)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      textAlign: 'center',
                    }}
                  >
                    Register
                  </Link>
                </div>
              </div>
            )}
          </>
        )}

        {!loading && error && (
          <Link
            to="/login"
            style={{
              display: 'inline-block',
              marginTop: '12px',
              color: 'var(--accent)',
              textDecoration: 'none',
              fontSize: '14px',
            }}
          >
            ← Back to Login
          </Link>
        )}
      </div>
    </div>
  );
}
